import type { Tracker, TrackerPagination } from "@/src/features/api/TrackerApi";
import { getTrackerReadAt } from "@/src/features/tracker/trackerReadWatermark";
import { useEffect, useMemo, useState } from "react";

import { getQuietTrackerCount } from "./trackerQuietCount";
import { isTrackerUnreadByWatermark } from "./tracker/trackerWatermarkLogic";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { pubsub } from "utils";
import { rootStore } from "@store";
import { trackerQueryKeys } from "./tracker";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { useQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";

export type TrackersPageMode = "no-trackers" | "no-signal" | "has-signals";

const toRows = (
  response:
    | { data?: TrackerPagination }
    | { data?: { data?: TrackerPagination } }
    | undefined,
): Tracker[] => {
  if (!response?.data) {
    return [];
  }
  const nested = (response.data as { data?: TrackerPagination }).data;
  if (nested?.rows) {
    return nested.rows;
  }
  return (response.data as TrackerPagination).rows ?? [];
};

/**
 * Backend-reported total, which can exceed the page `limit`. Falls back to the
 * fetched row count when the payload omits it.
 */
const toTotal = (
  response:
    | { data?: TrackerPagination }
    | { data?: { data?: TrackerPagination } }
    | undefined,
  fallbackRows: Tracker[],
): number => {
  if (!response?.data) {
    return fallbackRows.length;
  }
  const nested = (response.data as { data?: TrackerPagination }).data;
  const total = nested?.total ?? (response.data as TrackerPagination).total;
  return typeof total === "number" ? total : fallbackRows.length;
};

/**
 * F1 三态：无任务 / 有任务无新 Signal / 有新 Signal。
 * mode 完全由后端数据（含 fallback mock）决定，不依赖 URL 参数。
 * 两路 list 请求通过 Promise.all 并行拉取。
 */
export const useTrackersPageMode = (): {
  mode: TrackersPageMode | null;
  hasTrackers: boolean;
  hasSignals: boolean;
  findings: Tracker[];
  quietCount: number;
  trackerCount: number;
  showMyTrackersInHeader: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
} => {
  const { isLoading: isLoginLoading } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";
  const { trackerApi } = useOspApiClientProvider();
  const [watermarkVersion, setWatermarkVersion] = useState(0);

  useEffect(() => {
    const off = pubsub.on(pubSubKeyMap.TRACKER_READ_WATERMARK_UPDATED, () => {
      setWatermarkVersion((v) => v + 1);
    });
    return () => {
      off?.();
    };
  }, []);

  // Tracker read realtime sync is handled by the shared notification sync
  // coordinator (supabaseNotificationSyncCoordinator). No separate channel
  // is needed here — pubsub watermark updates are sufficient for local UI.

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: trackerQueryKeys.list({
      ownerId,
      limit: 10,
      unreadOnly: true,
      sortField: "last_triggered",
      sortOrder: "DESC",
    }),
    enabled: !!ownerId && !!trackerApi,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [fullListData, unreadOnlyData] = await Promise.all([
        trackerApi!.trackers.listTrackers({
          owner_id: ownerId,
          limit: 20,
          sort_field: "last_triggered",
          sort_order: "DESC",
        }),
        trackerApi!.trackers.listTrackers({
          owner_id: ownerId,
          limit: 10,
          unread_only: true,
          sort_field: "last_triggered",
          sort_order: "DESC",
        }),
      ]);

      return { fullListData, unreadOnlyData };
    },
  });

  const fullRows = toRows(data?.fullListData);
  const fullTotal = toTotal(data?.fullListData, fullRows);
  const unreadOnlyRows = toRows(data?.unreadOnlyData);
  return useMemo(() => {
    const isBootstrapPending = isLoginLoading || !ownerId || !trackerApi;
    if (isBootstrapPending) {
      return {
        mode: null,
        hasTrackers: false,
        hasSignals: false,
        findings: [],
        quietCount: 0,
        trackerCount: 0,
        showMyTrackersInHeader: false,
        isLoading: true,
        isFetching,
        isError,
        error,
      };
    }

    if (isLoading) {
      return {
        mode: null,
        hasTrackers: false,
        hasSignals: false,
        findings: [],
        quietCount: 0,
        trackerCount: 0,
        showMyTrackersInHeader: false,
        isLoading,
        isFetching,
        isError,
        error,
      };
    }

    const unreadRowsWithBackendCount = unreadOnlyRows.filter(
      (item) => Number(item.unread_count ?? 0) > 0,
    );
    const unreadRowsByWatermark = unreadRowsWithBackendCount.filter((item) => {
      const trackerId = item.tracker_id ?? "";
      const readAt = getTrackerReadAt(ownerId, trackerId);
      return isTrackerUnreadByWatermark(item, readAt);
    });
    const hasTrackers = fullRows.length > 0;
    const hasSignals = hasTrackers && unreadRowsWithBackendCount.length > 0;
    const quietCount = getQuietTrackerCount(fullRows, unreadRowsByWatermark);
    if (!hasTrackers) {
      return {
        mode: "no-trackers" as const,
        hasTrackers: false,
        hasSignals: false,
        findings: [],
        quietCount: 0,
        trackerCount: 0,
        showMyTrackersInHeader: false,
        isLoading,
        isFetching,
        isError,
        error,
      };
    }

    if (!hasSignals) {
      return {
        mode: "no-signal" as const,
        hasTrackers: true,
        hasSignals: false,
        findings: [],
        quietCount,
        trackerCount: fullTotal,
        showMyTrackersInHeader: true,
        isLoading,
        isFetching,
        isError,
        error,
      };
    }

    return {
      mode: "has-signals" as const,
      hasTrackers: true,
      hasSignals: true,
      // Temporary policy for联调:
      // Keep TrackersPage findings aligned with backend list payload so items
      // are not hidden immediately after local read watermark updates.
      // Bottom tab red dot still follows local watermark logic.
      findings: unreadRowsWithBackendCount
        ?.filter((item) => Number(item.unread_count ?? 0) > 0)
        .map((item) => {
          const isReadLocally = !unreadRowsByWatermark.some(
            (row) => row.tracker_id === item.tracker_id,
          );
          return isReadLocally ? { ...item, unread_count: 0 } : item;
        })
        .slice(0, 4),
      quietCount,
      trackerCount: fullTotal,
      showMyTrackersInHeader: true,
      isLoading,
      isFetching,
      isError,
      error,
    };
  }, [
    error,
    fullRows,
    fullTotal,
    isLoginLoading,
    isError,
    isFetching,
    isLoading,
    trackerApi,
    ownerId,
    unreadOnlyRows.length,
    watermarkVersion,
  ]);
};
